import { create } from "zustand";
import type { Session, User } from "@supabase/supabase-js";
import { supabase, isSupabaseConfigured } from "../lib/supabase";
import { api } from "../lib/api";

interface AuthState {
  session: Session | null;
  user: User | null;
  loading: boolean;
  initialized: boolean;
  devMode: boolean;
  pendingEmailConfirmation: string | null;

  initialize: () => Promise<void>;
  signInAsDev: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string) => Promise<void>;
  signInWithOAuth: (provider: "google" | "github") => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  clearPendingConfirmation: () => void;
  handleSessionExpired: () => Promise<void>;
}

let _sessionExpiredHandled = false;
let _backendSyncInFlight = false;

async function syncUserToBackend(): Promise<void> {
  if (_backendSyncInFlight) return;
  _backendSyncInFlight = true;
  try {
    await api.post("/api/v1/auth/callback");
  } catch {
    // Non-blocking: backend may be cold-starting or temporarily unavailable.
    // The user record will be created on the next successful call.
  } finally {
    _backendSyncInFlight = false;
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  session: null,
  user: null,
  loading: false,
  initialized: false,
  devMode: !isSupabaseConfigured,
  pendingEmailConfirmation: null,

  initialize: async () => {
    api.setUnauthorizedHandler(() => {
      get().handleSessionExpired();
    });

    if (!supabase) {
      const saved = localStorage.getItem("lever_dev_session");
      if (saved) {
        const devToken = "dev:dev-user:dev@lever.local";
        api.setToken(devToken);
        set({
          initialized: true,
          devMode: true,
          user: JSON.parse(saved),
        });
      } else {
        set({ initialized: true, devMode: true });
      }
      return;
    }

    try {
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        api.setToken(data.session.access_token);
        set({ session: data.session, user: data.session.user, initialized: true });
        syncUserToBackend();
      } else {
        set({ initialized: true });
      }
    } catch {
      set({ initialized: true });
    }

    supabase.auth.onAuthStateChange((event, session) => {
      api.setToken(session?.access_token ?? null);
      set({ session, user: session?.user ?? null });

      if (event === "SIGNED_IN" && session) {
        syncUserToBackend();
      }

      if (event === "SIGNED_OUT") {
        set({ session: null, user: null, pendingEmailConfirmation: null });
      }
    });
  },

  handleSessionExpired: async () => {
    if (_sessionExpiredHandled) return;
    _sessionExpiredHandled = true;

    const { user } = get();
    if (!user) return;

    api.setToken(null);
    set({ session: null, user: null });

    if (supabase) {
      try { await supabase.auth.signOut(); } catch { /* ignore */ }
    }
    localStorage.removeItem("lever_dev_session");

    for (const key of Object.keys(localStorage)) {
      if (key.startsWith("sb-") && key.endsWith("-auth-token")) {
        localStorage.removeItem(key);
      }
    }

    window.location.href = "/login?expired=1";
    setTimeout(() => { _sessionExpiredHandled = false; }, 3000);
  },

  signInAsDev: async () => {
    const devToken = "dev:dev-user:dev@lever.local";
    api.setToken(devToken);
    const devUser = {
      id: "dev-user",
      email: "dev@lever.local",
      user_metadata: { name: "Dev User" },
    } as unknown as User;
    localStorage.setItem("lever_dev_session", JSON.stringify(devUser));
    set({ user: devUser });
    syncUserToBackend();
  },

  signInWithEmail: async (email, password) => {
    if (!supabase) return;
    set({ loading: true });
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      api.setToken(data.session?.access_token ?? null);
      set({ session: data.session, user: data.user });
      syncUserToBackend();
    } finally {
      set({ loading: false });
    }
  },

  signUp: async (email, password, name) => {
    if (!supabase) return;
    set({ loading: true });
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { name } },
      });
      if (error) throw error;
      if (data.session) {
        api.setToken(data.session.access_token);
        set({ session: data.session, user: data.user });
        syncUserToBackend();
      } else {
        set({ pendingEmailConfirmation: email });
      }
    } finally {
      set({ loading: false });
    }
  },

  signInWithOAuth: async (provider) => {
    if (!supabase) return;
    set({ loading: true });
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: { redirectTo: `${window.location.origin}/auth/callback` },
      });
      if (error) throw error;
    } finally {
      set({ loading: false });
    }
  },

  resetPassword: async (email) => {
    if (!supabase) return;
    set({ loading: true });
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/login`,
      });
      if (error) throw error;
    } finally {
      set({ loading: false });
    }
  },

  signOut: async () => {
    if (supabase) {
      await supabase.auth.signOut();
    }
    localStorage.removeItem("lever_dev_session");
    api.setToken(null);
    set({ session: null, user: null, pendingEmailConfirmation: null });
  },

  clearPendingConfirmation: () => {
    set({ pendingEmailConfirmation: null });
  },
}));
